/* ============================================================
   DMC Wholesale — Bulk Orders page engine
   Core table/selection logic + fast Weekly Ads export engine.

   Why this is faster than the old version:
   1. Product data is fetched ONCE (chunked, parallel, cached) instead
      of once per step plus once per product for the image step.
   2. Images are requested at the size they are actually drawn
      (Shopify CDN ?width=) and prefetched in the background, instead
      of downloading full-size originals during every render.
   3. The 4-per-page sheets are rendered ONCE and reused for both the
      4/page PDF and the split page images (previously rendered twice).
   4. html2canvas clones a trimmed document (no theme DOM) and runs
      several renders in parallel; all fixed delay()/pause calls are gone.
   5. Images are bundled into one ZIP instead of 100+ separate downloads.
   ============================================================ */

/* ---------------- small helpers ---------------- */
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}
function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
function chunk(arr, n) {
  var out = [];
  for (var i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}
function htmlToText(html) {
  if (!html) return '';
  try { return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim(); }
  catch (e) { return String(html).replace(/<[^>]+>/g, ''); }
}
function truncateDesc(text, maxLen) {
  if (!text) return 'No description available';
  return text.length <= maxLen ? text : text.slice(0, maxLen).trimEnd() + '...';
}
function safeFileName(s) {
  return String(s || 'Product').replace(/[^a-z0-9_\-]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'Product';
}
function fmtDur(ms) {
  var s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? s + 's' : Math.floor(s / 60) + 'm ' + String(s % 60).padStart(2, '0') + 's';
}
function fmtStamp() {
  var d = new Date(), p = function (n) { return String(n).padStart(2, '0'); };
  return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + '_' + p(d.getHours()) + p(d.getMinutes());
}
/* Ask the Shopify CDN for an image at the size we actually need. */
function sizedUrl(url, w) {
  if (!url) return '';
  try {
    var u = new URL(url, location.href);
    if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) u.searchParams.set('width', w);
    return u.href;
  } catch (e) { return url; }
}
function getSelectedIds() {
  try { return JSON.parse(localStorage.getItem('selectedProducts') || '[]'); } catch (e) { return []; }
}
function setSelectedIds(arr) { localStorage.setItem('selectedProducts', JSON.stringify(arr)); }

/* ---------------- selection / table ---------------- */
function updateSelectedCount() {
  var el = document.getElementById('selected-count');
  if (el) el.textContent = getSelectedIds().length;
}
function restoreChecks() {
  var s = new Set(getSelectedIds());
  document.querySelectorAll('.product-checkbox').forEach(function (cb) { cb.checked = s.has(cb.value); });
}
function selectAllOnPage() {
  var s = new Set(getSelectedIds());
  document.querySelectorAll('.product-checkbox').forEach(function (cb) { cb.checked = true; s.add(cb.value); });
  setSelectedIds([...s]);
  updateSelectedCount();
}
function deselectAllProducts() {
  localStorage.removeItem('selectedProducts');
  document.querySelectorAll('.product-checkbox').forEach(function (cb) { cb.checked = false; });
  document.querySelectorAll('.order-qty-input').forEach(function (i) { i.value = 0; });
  updateSelectedCount();
}
function initTableEvents() {
  var tbody = document.getElementById('product-table-body');
  if (!tbody) return;
  /* One delegated listener covers initial rows AND rows rebuilt by search/collection filters. */
  tbody.addEventListener('change', function (e) {
    var t = e.target;
    if (t.classList.contains('product-checkbox')) {
      var s = new Set(getSelectedIds());
      t.checked ? s.add(t.value) : s.delete(t.value);
      setSelectedIds([...s]);
      updateSelectedCount();
    } else if (t.classList.contains('order-qty-input')) {
      var qty = parseInt(t.value || '0', 10);
      if (qty > 0) {
        var cb = tbody.querySelector('.product-checkbox[data-product-id="' + t.getAttribute('data-product-id') + '"]');
        if (cb) {
          cb.checked = true;
          var s2 = new Set(getSelectedIds());
          s2.add(cb.value);
          setSelectedIds([...s2]);
          updateSelectedCount();
        }
      }
    }
  });
}
function sortProducts(criteria) {
  var tbody = document.getElementById('product-table-body');
  var rows = Array.from(tbody.querySelectorAll('.product-row'));
  rows.sort(function (a, b) {
    if (criteria === 'name') return a.getAttribute('data-title').localeCompare(b.getAttribute('data-title'));
    if (criteria === 'priceLow') return parseFloat(a.getAttribute('data-price')) - parseFloat(b.getAttribute('data-price'));
    if (criteria === 'priceHigh') return parseFloat(b.getAttribute('data-price')) - parseFloat(a.getAttribute('data-price'));
    if (criteria === 'date') return new Date(b.getAttribute('data-date')) - new Date(a.getAttribute('data-date'));
    if (criteria === 'type') return a.getAttribute('data-type').localeCompare(b.getAttribute('data-type'));
    return 0;
  });
  rows.forEach(function (r) { tbody.appendChild(r); });
}
function toggleClearButton() {
  var input = document.getElementById('product-search');
  var btn = document.getElementById('clear-search');
  if (btn && input) btn.style.display = input.value ? 'block' : 'none';
}
function setPaginationVisible(show) {
  document.querySelectorAll('.grid-pagination').forEach(function (p) { p.style.display = show ? '' : 'none'; });
}

/* The original server-rendered rows are saved once, so clearing a search
   restores them instantly instead of re-rendering the Liquid loop in JS. */
var initialRowsHTML = null;
function restoreInitialRows() {
  var tbody = document.getElementById('product-table-body');
  if (!tbody) return;
  if (initialRowsHTML !== null) tbody.innerHTML = initialRowsHTML;
  setPaginationVisible(true);
  restoreChecks();
  updateSelectedCount();
  var s = document.getElementById('sort-select').value;
  if (s) sortProducts(s);
}
function clearSearch() {
  var input = document.getElementById('product-search');
  input.value = ''; input.focus(); toggleClearButton();
  _filterSeq++; _collSeq++;
  restoreInitialRows();
}
function buildRow(productId, title, handle, imageUrl, description, wholesale, rrp, extra) {
  var row = document.createElement('tr');
  row.className = 'product-row';
  row.setAttribute('data-title', ((extra && extra.title) || title).toLowerCase());
  row.setAttribute('data-price', (extra && extra.price) || '');
  row.setAttribute('data-date', (extra && extra.date) || '');
  row.setAttribute('data-type', ((extra && extra.type) || '').toLowerCase());
  var desc = description || '';
  row.innerHTML =
    '<td class="cell-checkbox"><input type="checkbox" class="product-checkbox" value="' + esc(productId) + '" data-product-id="' + esc(productId) + '"></td>' +
    '<td class="cell-name"><a href="/products/' + esc(handle) + '" class="product-link">' + esc(title) + '</a></td>' +
    '<td class="cell-image"><img src="' + esc(sizedUrl(imageUrl, 150)) + '" alt="' + esc(title) + '" loading="lazy"></td>' +
    '<td class="cell-description">' + esc(desc.slice(0, 120)) + (desc.length > 120 ? '...' : '') + '</td>' +
    '<td class="cell-wholesale"><span class="wholesale-price">R' + wholesale + '</span></td>' +
    '<td class="cell-rrp">R' + rrp + '</td>' +
    '<td class="cell-qty"><input type="number" min="0" value="0" class="order-qty-input" data-product-id="' + esc(productId) + '"></td>' +
    '<td class="cell-info">' +
      '<div class="desktop-actions"><button onclick="showProductInfo(\'' + esc(productId) + '\')" class="btn-info adjustable-btn">ℹ️ Info</button></div>' +
      '<div class="mobile-actions-row"><button onclick="showProductInfo(\'' + esc(productId) + '\')" class="btn-info adjustable-btn">ℹ️ Info</button></div>' +
    '</td>';
  return row;
}

/* ---- search (debounced, stale-response safe, no image payload) ---- */
var _filterTimer = null, _filterSeq = 0;
function filterProducts() {
  clearTimeout(_filterTimer);
  _filterTimer = setTimeout(runProductSearch, 300);
}
async function runProductSearch() {
  var q = document.getElementById('product-search').value.trim().toLowerCase();
  if (!q) { _filterSeq++; restoreInitialRows(); return; }
  var seq = ++_filterSeq;
  var gql = 'query($query:String!){products(first:50,query:$query){edges{node{id title handle description productType createdAt featuredImage{url} priceRange{minVariantPrice{amount}} variants(first:1){edges{node{id availableForSale}}}}}}}';
  try {
    var data = await storefrontQuery(gql, { query: 'title:*' + q + '*' });
    if (seq !== _filterSeq) return;
    var products = (data.products && data.products.edges || []).map(function (e) { return e.node; });
    var tbody = document.getElementById('product-table-body');
    tbody.innerHTML = '';
    products.forEach(function (p) {
      var v = p.variants && p.variants.edges && p.variants.edges[0] && p.variants.edges[0].node;
      if (!v || !v.availableForSale) return;
      var pid = p.id.split('/').pop();
      var price = parseFloat(p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount || '0') || 0;
      tbody.appendChild(buildRow(pid, p.title, p.handle, p.featuredImage && p.featuredImage.url || '', p.description || '', (price * 0.8).toFixed(2), price.toFixed(2), { price: price, date: p.createdAt, type: p.productType }));
    });
    setPaginationVisible(false);
    restoreChecks();
    var s = document.getElementById('sort-select').value; if (s) sortProducts(s);
  } catch (e) { console.error('Search failed:', e); }
}

/* ---- collection filter ---- */
var _collSeq = 0;
async function filterByCollection(handle) {
  if (!handle) { clearSearch(); return; }
  var seq = ++_collSeq;
  var tbody = document.getElementById('product-table-body');
  tbody.style.opacity = '0.5';
  var page = 1, products = [];
  for (;;) {
    try {
      var res = await fetch('/collections/' + handle + '/products.json?limit=250&page=' + page);
      if (!res.ok) break;
      var batch = (await res.json()).products || [];
      products = products.concat(batch);
      if (batch.length < 250) break;
      page++;
    } catch (e) { console.error(e); break; }
  }
  if (seq !== _collSeq) return;
  tbody.innerHTML = '';
  products.forEach(function (p) {
    var v = p.variants && p.variants[0];
    if (!v || !v.available) return;
    var price = parseFloat(v.price || '0') || 0;
    var img = p.images && p.images[0] ? p.images[0].src : '';
    tbody.appendChild(buildRow(p.id, p.title, p.handle, img, htmlToText(p.body_html), (price * 0.8).toFixed(2), price.toFixed(2), { price: price, date: p.created_at, type: p.product_type }));
  });
  tbody.style.opacity = '';
  setPaginationVisible(false);
  restoreChecks();
  var s = document.getElementById('sort-select').value; if (s) sortProducts(s);
}

/* ---------------- pricing ---------------- */
var pricingMode = '25';
function setPricingMode(mode) { pricingMode = mode; }
function calcSellingPrice(basePrice, mu) {
  if (mu === 25) return basePrice;
  return Math.round(basePrice * 0.8 * (1 + mu / 100) / 10) * 10 - 1;
}

/* ---------------- Storefront API (chunked, parallel, cached) ---------------- */
var PRODUCT_GQL = 'query getProducts($ids:[ID!]!){nodes(ids:$ids){...on Product{id title handle description featuredImage{url} images(first:250){edges{node{url}}} priceRange{minVariantPrice{amount currencyCode}} compareAtPriceRange{maxVariantPrice{amount currencyCode}} variants(first:1){edges{node{id availableForSale}}}}}}';
var productCache = new Map();

async function storefrontQuery(query, variables, tries) {
  tries = tries || 3;
  var lastErr;
  for (var a = 0; a < tries; a++) {
    try {
      var res = await fetch('/api/2023-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN },
        body: JSON.stringify({ query: query, variables: variables })
      });
      if (res.status === 429 || res.status >= 500) throw new Error('HTTP ' + res.status);
      var json = await res.json();
      if (!json.data) throw new Error((json.errors && json.errors[0] && json.errors[0].message) || 'GraphQL error');
      return json.data;
    } catch (e) { lastErr = e; if (a < tries - 1) await delay(400 * Math.pow(2, a)); }
  }
  throw lastErr;
}

async function runPool(items, limit, worker) {
  var results = new Array(items.length), next = 0;
  async function lane() {
    for (;;) {
      if (bulkCancel) return;
      var i = next++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  }
  var lanes = [];
  for (var l = 0; l < Math.min(limit, items.length); l++) lanes.push(lane());
  await Promise.all(lanes);
  return results;
}

/* Returns nodes in the same order as `gids`. Cached for the page session. */
async function fetchProductsByIds(gids, opts) {
  var fresh = opts && opts.fresh;
  var need = gids.filter(function (g) { return fresh || !productCache.has(g); });
  var chunks = chunk(need, 25);
  var wasCancel = bulkCancel; /* data fetch must not be skipped by a stale cancel flag */
  bulkCancel = false;
  try {
    await runPool(chunks, 4, async function (c) {
      var data = await storefrontQuery(PRODUCT_GQL, { ids: c });
      var nodes = data.nodes || [];
      c.forEach(function (g, i) { productCache.set(g, nodes[i] || null); });
    });
  } finally { bulkCancel = wasCancel; }
  return gids.map(function (g) { return productCache.get(g) || null; });
}

async function addSelectionToCart() {
  var ids = getSelectedIds();
  if (!ids.length) { alert('No products selected.'); return; }
  var products = await fetchProductsByIds(ids.map(function (id) { return 'gid://shopify/Product/' + id; }), { fresh: true });
  var items = [];
  products.forEach(function (p) {
    if (!p) return;
    var v = p.variants && p.variants.edges && p.variants.edges[0] && p.variants.edges[0].node;
    if (!v || !v.availableForSale) return;
    var pid = p.id.split('/').pop();
    var qEl = document.querySelector('.order-qty-input[data-product-id="' + pid + '"]');
    var qty = qEl ? parseInt(qEl.value || '0', 10) : 0;
    if (!qty || qty < 1) qty = 1;
    items.push({ id: v.id.split('/').pop(), quantity: qty });
  });
  if (!items.length) { alert('No available variants to add.'); return; }
  try {
    var res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) });
    if (!res.ok) throw new Error();
    (window.Shopify && Shopify.theme && Shopify.theme.cartDrawer) ? Shopify.theme.cartDrawer.open() : (window.location.href = '/cart');
  } catch (e) { alert('Failed to add products to cart.'); }
}

/* ---------------- Info popup ---------------- */
var _lastPopupData = null;

function allImagesOf(p) {
  var a = ((p.images && p.images.edges) || []).map(function (e) { return e && e.node && e.node.url; }).filter(Boolean);
  if (!a.length && p.featuredImage && p.featuredImage.url) a.push(p.featuredImage.url);
  return a;
}
function extraThumbs(all, max) { return all.length > 1 ? all.slice(2).reverse().slice(0, max) : []; }
function productBasePrice(p) { return parseFloat(p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount || '0') || 0; }
function productCompareAt(p, base) {
  var cap = p.compareAtPriceRange && p.compareAtPriceRange.maxVariantPrice && p.compareAtPriceRange.maxVariantPrice.amount;
  var n = cap ? parseFloat(cap) : 0;
  return n > base ? n.toFixed(2) : null; /* only show a "Was" price that is higher than the current price */
}
function buildPopupData(product) {
  var allImages = allImagesOf(product);
  var rawDesc = product.description || 'No description available';
  var base = productBasePrice(product);
  var compareAtPrice = productCompareAt(product, base);
  var priceHtml = '', compareHtml = '';
  if (pricingMode !== 'none') {
    var mu = parseInt(pricingMode, 10);
    var sp = calcSellingPrice(base, mu);
    if (mu === 25) {
      priceHtml = '<div class="popup-price">Now Only: R' + base.toFixed(2) + '</div>';
      if (compareAtPrice) compareHtml = '<div class="popup-compare">Was: R' + compareAtPrice + '</div>';
    } else {
      priceHtml = '<div class="popup-price">R' + (Number.isInteger(sp) ? sp : sp.toFixed(2)) + '</div>';
    }
  }
  return { title: product.title || 'Product', allImages: allImages, rawDesc: rawDesc, priceHtml: priceHtml, compareHtml: compareHtml };
}

async function showProductInfo(productId) {
  var popup = document.getElementById('product-popup');
  popup.style.top = window.scrollY + 'px';
  var product = null;
  try { product = (await fetchProductsByIds(['gid://shopify/Product/' + productId]))[0]; } catch (e) {}

  var variant = product && product.variants && product.variants.edges && product.variants.edges[0] && product.variants.edges[0].node;
  if (!product || !variant || !variant.availableForSale) {
    document.getElementById('popup-inner').innerHTML = '<div class="images"></div><h2>Quick view not available</h2><div class="description">This product does not have preview content.</div>';
    _lastPopupData = null;
    popup.style.display = 'flex';
    return;
  }

  var isMobile = window.innerWidth <= 768;
  var data = buildPopupData(product);
  var all = data.allImages, imagesHtml = '', thumbsHtml = '';
  if (all.length === 1) {
    imagesHtml = '<div class="images single"><img src="' + esc(sizedUrl(all[0], 800)) + '" alt="Product"></div>';
  } else if (all.length > 1) {
    imagesHtml = '<div class="images"><img src="' + esc(sizedUrl(all[0], 800)) + '" alt="Product"></div>';
    var extras = extraThumbs(all, isMobile ? 3 : 5);
    if (extras.length) thumbsHtml = '<div class="thumbnails">' + extras.map(function (u) {
      return '<img src="' + esc(sizedUrl(u, 240)) + '" alt="Thumb" onclick="swapMainImage(\'' + esc(u) + '\')">';
    }).join('') + '</div>';
  }
  var displayDesc = isMobile ? truncateDesc(data.rawDesc, 250) : data.rawDesc;
  _lastPopupData = data;
  document.getElementById('popup-inner').innerHTML =
    '<h2>' + esc(data.title) + '</h2>' + imagesHtml + thumbsHtml + data.priceHtml + data.compareHtml +
    '<div class="description">' + esc(displayDesc) + '</div>';
  popup.style.display = 'flex';
}
function swapMainImage(url) {
  var c = document.querySelector('#popup-inner .images');
  if (c) c.innerHTML = '<img src="' + esc(sizedUrl(url, 800)) + '" alt="Product">';
}
function closePopup() {
  var p = document.getElementById('product-popup');
  if (p) p.style.display = 'none';
}
document.addEventListener('click', function (e) {
  var popup = document.getElementById('product-popup');
  var content = document.querySelector('.popup-content');
  if (popup && popup.style.display === 'flex' && content && !content.contains(e.target) && !e.target.closest('.btn-info')) closePopup();
});

/* ---------------- progress overlay + bulk-op guard ---------------- */
var bulkOpRunning = false, bulkCancel = false;
var _progT0 = 0, _progDone = 0, _progTotal = 0, _progLabel = '';

function setExportButtonsDisabled(d) {
  document.querySelectorAll('.btn-export, .btn-select, .btn-deselect').forEach(function (b) { b.disabled = d; });
}
function _el(id) { return document.getElementById(id); }
function showProgress(title) {
  _progT0 = Date.now(); _progDone = 0; _progTotal = 0; _progLabel = 'Starting…';
  var ov = _el('bulk-progress-overlay');
  ov.classList.remove('is-done');
  _el('bulk-progress-title').textContent = title || 'Working…';
  _el('bulk-cancel-btn').style.display = ''; _el('bulk-cancel-btn').disabled = false;
  _el('bulk-close-btn').style.display = 'none';
  renderProgress();
  ov.style.display = 'flex';
}
function setProgressPlan(total) { _progTotal = total; renderProgress(); }
function setProgressLabel(label) { _progLabel = label; renderProgress(); }
function tickProgress(n) { _progDone += (n || 1); renderProgress(); }
function renderProgress() {
  var pct = _progTotal ? Math.min(100, (_progDone / _progTotal) * 100) : 0;
  _el('bulk-progress-bar').style.width = pct.toFixed(1) + '%';
  _el('bulk-progress-text').textContent = _progLabel;
  var meta = '';
  if (_progTotal) {
    meta = _progDone + ' / ' + _progTotal + ' · ' + fmtDur(Date.now() - _progT0);
    if (_progDone >= 3 && _progDone < _progTotal) meta += ' · ~' + fmtDur((Date.now() - _progT0) / _progDone * (_progTotal - _progDone)) + ' left';
  }
  _el('bulk-progress-meta').textContent = meta;
}
function finishProgress(summaryText) {
  var ov = _el('bulk-progress-overlay');
  ov.classList.add('is-done');
  _el('bulk-progress-title').textContent = '✅ Done in ' + fmtDur(Date.now() - _progT0);
  _el('bulk-progress-bar').style.width = '100%';
  _el('bulk-progress-text').textContent = summaryText;
  _el('bulk-progress-meta').textContent = '';
  _el('bulk-cancel-btn').style.display = 'none';
  _el('bulk-close-btn').style.display = '';
}
function hideProgress() { var ov = _el('bulk-progress-overlay'); if (ov) ov.style.display = 'none'; }
function closeProgress() { hideProgress(); }
function cancelBulkOp() {
  bulkCancel = true;
  _el('bulk-cancel-btn').disabled = true;
  setProgressLabel('Cancelling… finishing the items in progress');
}
function beginBulkOp() {
  if (bulkOpRunning) return false;
  bulkOpRunning = true; bulkCancel = false;
  setExportButtonsDisabled(true);
  return true;
}
function endBulkOp() {
  bulkOpRunning = false;
  setExportButtonsDisabled(false);
  _prefetchToken++;       /* stop background image prefetch */
  imgCache.clear();
}

/* ---------------- image preload / render helpers ---------------- */
var imgCache = new Map(), _prefetchToken = 0, _capSeq = 0;
var EXPORT_SCALE = 1.6;
var Q_SINGLE = 0.90, Q_QUAD = 0.88, Q_POPUP = 0.90;
var RENDER_CONCURRENCY = Math.max(1, Math.min(3, Math.floor((navigator.hardwareConcurrency || 4) / 2)));
var IMG_MAIN_W = 1100, IMG_QUAD_W = 640, IMG_THUMB_W = 240;

function preloadImage(url) {
  if (!url) return Promise.resolve(false);
  if (imgCache.has(url)) return imgCache.get(url);
  var p = new Promise(function (res) {
    var im = new Image(); im.crossOrigin = 'anonymous';
    var t = setTimeout(function () { res(false); }, 15000);
    im.onload = function () { clearTimeout(t); res(true); };
    im.onerror = function () { clearTimeout(t); res(false); };
    im.src = url;
  });
  imgCache.set(url, p);
  return p;
}
function prefetchImagesInBackground(urls) {
  var token = ++_prefetchToken, i = 0;
  async function lane() {
    while (token === _prefetchToken) {
      var k = i++;
      if (k >= urls.length) return;
      await preloadImage(urls[k]);
    }
  }
  for (var l = 0; l < 8; l++) lane();
}
function waitForImages(root, timeout) {
  var imgs = Array.from(root.querySelectorAll('img')).filter(function (i) { return !i.complete; });
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(imgs.map(function (i) { return new Promise(function (r) { i.addEventListener('load', r, { once: true }); i.addEventListener('error', r, { once: true }); }); })),
    delay(timeout || 12000)
  ]);
}
function releaseCanvas(canvas) { try { canvas.width = 0; canvas.height = 0; } catch (e) {} }

/* html2canvas clones the whole page for every capture. Trimming the clone to just
   the card (keeping <style> blocks) makes every render much cheaper. */
function trimClone(doc, id) {
  try {
    doc.querySelectorAll('body style').forEach(function (s) { doc.head.appendChild(s); });
    Array.from(doc.body.children).forEach(function (c) { if (c.getAttribute('data-cap-id') !== id) c.remove(); });
  } catch (e) {}
}
async function captureNode(node, id) {
  await waitForImages(node);
  return html2canvas(node, {
    backgroundColor: '#ffffff', scale: EXPORT_SCALE, useCORS: true, allowTaint: false,
    imageTimeout: 15000, logging: false, onclone: function (doc) { trimClone(doc, id); }
  });
}
async function renderCardToJpeg(html, w, h, quality) {
  var id = 'cap' + (++_capSeq);
  var host = document.createElement('div');
  host.setAttribute('data-cap-id', id);
  host.style.cssText = 'position:fixed;top:-99999px;left:-99999px;' + (w ? 'width:' + w + 'px;' : '') + (h ? 'height:' + h + 'px;' : '');
  host.innerHTML = html;
  document.body.appendChild(host);
  try {
    var canvas = await captureNode(host, id);
    var url = canvas.toDataURL('image/jpeg', quality);
    releaseCanvas(canvas);
    return url;
  } finally { host.remove(); }
}

/* ---------------- card builders (match the Info popup design) ---------------- */
function getCardPricingHtml(basePrice, compareAtPrice, priceFontPx, compareFontPx) {
  if (pricingMode === 'none') return { priceHtml: '', compareHtml: '' };
  var mu = parseInt(pricingMode, 10), priceHtml = '', compareHtml = '';
  if (mu === 25) {
    priceHtml = '<div style="color:#e00000;font-weight:900;font-size:' + priceFontPx + 'px;text-align:center;margin:' + Math.round(priceFontPx * 0.2) + 'px 0 0;letter-spacing:-0.5px;">Now Only: R' + basePrice.toFixed(2) + '</div>';
    if (compareAtPrice) compareHtml = '<div style="color:#666;text-decoration:line-through;font-size:' + compareFontPx + 'px;text-align:center;margin:0 0 ' + Math.round(compareFontPx * 0.6) + 'px;">Was: R' + compareAtPrice + '</div>';
  } else {
    var sp = calcSellingPrice(basePrice, mu);
    priceHtml = '<div style="color:#e00000;font-weight:900;font-size:' + priceFontPx + 'px;text-align:center;margin:' + Math.round(priceFontPx * 0.2) + 'px 0 0;letter-spacing:-0.5px;">R' + (Number.isInteger(sp) ? sp : sp.toFixed(2)) + '</div>';
  }
  return { priceHtml: priceHtml, compareHtml: compareHtml };
}

function buildProductCardHTML(product, opts) {
  var all = allImagesOf(product);
  var mainImg = sizedUrl(all[0] || '', opts.mainW);
  var thumbsHtml = '';
  var extras = extraThumbs(all, 5);
  if (extras.length) {
    thumbsHtml = '<div style="display:flex;justify-content:center;gap:' + opts.thumbGap + 'px;flex-wrap:wrap;margin-bottom:' + opts.thumbMarginBottom + 'px;width:100%;">' +
      extras.map(function (u) {
        return '<img crossorigin="anonymous" src="' + esc(sizedUrl(u, IMG_THUMB_W)) + '" style="width:' + opts.thumbSize + 'px;height:' + Math.round(opts.thumbSize * 0.75) + 'px;object-fit:cover;border:1px solid #ccc;border-radius:4px;">';
      }).join('') + '</div>';
  }
  var base = productBasePrice(product);
  var pr = getCardPricingHtml(base, productCompareAt(product, base), opts.priceFont, opts.compareFont);
  var desc = truncateDesc((product.description || '').replace(/\s+/g, ' ').trim(), opts.descChars);
  return '<div style="width:100%;height:100%;box-sizing:border-box;padding:' + opts.padding + ';display:flex;flex-direction:column;align-items:center;font-family:Arial,Helvetica,sans-serif;background:#ffffff;overflow:hidden;">' +
    '<h1 style="font-size:' + opts.titleFont + 'px;font-weight:800;color:#000000;text-align:center;margin:0 0 ' + opts.titleMarginBottom + 'px;line-height:1.15;width:100%;">' + esc(product.title || '') + '</h1>' +
    '<div style="width:100%;display:flex;justify-content:center;align-items:center;margin-bottom:' + opts.imgMarginBottom + 'px;flex:1 1 auto;min-height:0;overflow:hidden;">' +
      (mainImg ? '<img crossorigin="anonymous" src="' + esc(mainImg) + '" style="max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;">' : '') +
    '</div>' +
    thumbsHtml + pr.priceHtml + pr.compareHtml +
    '<p style="font-size:' + opts.descFont + 'px;line-height:1.5;color:#444444;text-align:center;margin:' + opts.descMarginTop + 'px auto 0;max-width:' + opts.descMaxWidth + 'px;">' + esc(desc) + '</p>' +
  '</div>';
}

var SINGLE_PAGE_OPTS = {
  padding: '46px 55px', titleFont: 34, titleMarginBottom: 26,
  imgW: 440, imgMaxH: 400, imgMarginBottom: 18,
  thumbGap: 10, thumbSize: 92, thumbMarginBottom: 18,
  priceFont: 30, compareFont: 16,
  descFont: 14, descMarginTop: 26, descMaxWidth: 600, descChars: 420, mainW: IMG_MAIN_W
};
var QUAD_OPTS = {
  padding: '16px 18px', titleFont: 17, titleMarginBottom: 10,
  imgW: 210, imgMaxH: 165, imgMarginBottom: 8,
  thumbGap: 4, thumbSize: 42, thumbMarginBottom: 8,
  priceFont: 16, compareFont: 9,
  descFont: 8, descMarginTop: 8, descMaxWidth: 300, descChars: 170, mainW: IMG_QUAD_W
};
var PDF_PAGE_W = 800;
var PDF_PAGE_H = 1131; /* A4 portrait aspect ratio */

function buildQuadPageHTML(productsChunk) {
  var cells = productsChunk.map(function (p) {
    return '<div style="width:calc(50% - 8px);height:calc(50% - 8px);box-sizing:border-box;border:1px solid #e5e5e5;border-radius:6px;overflow:hidden;">' + buildProductCardHTML(p, QUAD_OPTS) + '</div>';
  }).join('');
  return '<div style="width:' + PDF_PAGE_W + 'px;height:' + PDF_PAGE_H + 'px;box-sizing:border-box;padding:16px;display:flex;flex-wrap:wrap;justify-content:space-between;align-content:space-between;gap:16px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;">' + cells + '</div>';
}

/* Standalone product image (the "Save as Image" look). Always desktop size. */
function buildPopupCloneNode(data, id) {
  var imgs = data.allImages, imagesHtml = '', thumbsHtml = '';
  if (imgs.length === 1) {
    imagesHtml = '<div class="images single"><img crossorigin="anonymous" src="' + esc(sizedUrl(imgs[0], IMG_MAIN_W)) + '" alt="Product"></div>';
  } else if (imgs.length > 1) {
    imagesHtml = '<div class="images"><img crossorigin="anonymous" src="' + esc(sizedUrl(imgs[0], IMG_MAIN_W)) + '" alt="Product"></div>';
    var extras = extraThumbs(imgs, 5);
    if (extras.length) thumbsHtml = '<div class="thumbnails">' + extras.map(function (u) { return '<img crossorigin="anonymous" src="' + esc(sizedUrl(u, IMG_THUMB_W)) + '" alt="Thumb">'; }).join('') + '</div>';
  }
  var clone = document.createElement('div');
  clone.className = 'popup-save-clone';
  clone.setAttribute('data-cap-id', id);
  clone.innerHTML = '<div class="clone-inner"><h2>' + esc(data.title) + '</h2>' + imagesHtml + thumbsHtml + data.priceHtml + data.compareHtml +
    '<div class="description">' + esc(truncateDesc(data.rawDesc, 300)) + '</div></div>';
  return clone;
}
async function renderPopupToJpeg(data, quality) {
  var id = 'cap' + (++_capSeq);
  var node = buildPopupCloneNode(data, id);
  document.body.appendChild(node);
  try {
    var canvas = await captureNode(node, id);
    var url = canvas.toDataURL('image/jpeg', quality);
    releaseCanvas(canvas);
    return url;
  } finally { node.remove(); }
}

/* ---------------- downloads ---------------- */
function downloadHref(href, filename, revoke) {
  var a = document.createElement('a');
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  if (revoke) setTimeout(function () { URL.revokeObjectURL(href); }, 15000);
}
function downloadBlob(blob, filename) { downloadHref(URL.createObjectURL(blob), filename, true); }
function downloadDataUrl(dataUrl, filename) { downloadHref(dataUrl, filename, false); }
function savePdfDoc(doc, baseName) {
  var fn = baseName + '_' + fmtStamp() + '.pdf';
  try { doc.save(fn); } catch (e) { downloadBlob(doc.output('blob'), fn); }
  return fn;
}

var _jszipPromise = null;
function loadJsZip() {
  if (window.JSZip) return Promise.resolve(window.JSZip);
  if (_jszipPromise) return _jszipPromise;
  _jszipPromise = new Promise(function (res) {
    var s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    s.onload = function () { res(window.JSZip || null); };
    s.onerror = function () { res(null); };
    document.head.appendChild(s);
  });
  return _jszipPromise;
}

/* Collects JPEGs: into one ZIP when JSZip is available, otherwise individual downloads. */
function createImageSink(JSZipLib, zipBaseName, stamp) {
  var zip = JSZipLib ? new JSZipLib() : null, count = 0;
  return {
    usesZip: !!zip,
    count: function () { return count; },
    add: async function (folder, name, dataUrl) {
      count++;
      if (zip) zip.folder(folder).file(name, dataUrl.slice(dataUrl.indexOf(',') + 1), { base64: true });
      else { downloadDataUrl(dataUrl, name.replace(/\.jpg$/i, '') + '_' + stamp + '.jpg'); await delay(150); }
    },
    finish: async function () {
      if (!zip || !count) return null;
      var blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' }, function (m) { setProgressLabel('Building ZIP… ' + Math.round(m.percent) + '%'); });
      var fn = zipBaseName + '_' + stamp + '.zip';
      downloadBlob(blob, fn);
      return fn;
    }
  };
}

/* Info popup "Save as Image" button */
async function savePopupAsImage(customName) {
  if (!_lastPopupData) return;
  var data = _lastPopupData;
  try {
    var url = await renderPopupToJpeg(data, Q_POPUP);
    downloadDataUrl(url, safeFileName(customName || data.title || 'Product_Info') + '_' + Date.now() + '.jpg');
  } catch (e) { console.error(e); alert('Could not save the image.'); }
}
async function saveSingleProductImage(productId) {
  var p = (await fetchProductsByIds(['gid://shopify/Product/' + productId]))[0];
  if (!p) return;
  var url = await renderPopupToJpeg(buildPopupData(p), Q_POPUP);
  downloadDataUrl(url, safeFileName(p.title) + '_' + Date.now() + '.jpg');
}

/* ============================================================
   EXPORT ENGINE
   One run = fetch once -> prefetch images -> render stages in parallel.
   flags: pdfSingle, pdfQuad, quadImages, popupImages, weekly
   ============================================================ */
async function runExports(o) {
  if (bulkOpRunning) { alert('An export is already running — please wait for it to finish.'); return 'busy'; }
  var ids = getSelectedIds();
  if (!ids.length) { alert('No products selected.'); return 'no-selection'; }
  var needsPages = o.pdfSingle || o.pdfQuad || o.quadImages;
  if (needsPages && ids.length > 150) { alert('Max 150 products at a time.'); return 'too-many'; }
  if ((o.pdfSingle || o.pdfQuad) && !(window.jspdf && window.jspdf.jsPDF)) { alert('PDF library is not loaded on this page.'); return 'no-lib'; }
  if (typeof html2canvas === 'undefined') { alert('Image library (html2canvas) is not loaded on this page.'); return 'no-lib'; }
  if (o.weekly && !confirm('Weekly Ads will create the full pack for ' + ids.length + ' selected product(s): PDF, PDF (4/page), 4/page images and product images. Continue?')) return 'cancelled';
  if (!o.weekly && o.popupImages && !needsPages && ids.length > 40 && !confirm('More than 40 products selected. Continue?')) return 'cancelled';
  if (!beginBulkOp()) return 'busy';

  var qEl = document.getElementById('export-quality');
  EXPORT_SCALE = (qEl && parseFloat(qEl.value)) || 1.6;
  var zipEl = document.getElementById('export-zip');
  var wantsImages = o.quadImages || o.popupImages;
  var useZip = wantsImages && (!zipEl || zipEl.checked);
  var stamp = fmtStamp();
  var failures = 0, summary = [];

  showProgress(o.weekly ? '📅 Weekly Ads' : '⚙️ Exporting');
  try {
    setProgressLabel('Fetching product data…');
    var zipPromise = useZip ? loadJsZip() : Promise.resolve(null);
    var all = await fetchProductsByIds(ids.map(function (id) { return 'gid://shopify/Product/' + id; }));
    var products = all.filter(function (p) { return p && p.variants && p.variants.edges && p.variants.edges[0] && p.variants.edges[0].node.availableForSale; });
    if (!products.length) { hideProgress(); alert('No available products to export.'); return 'no-available'; }

    var quadChunks = chunk(products, 4);
    var doQuad = o.pdfQuad || o.quadImages;
    if (o.quadImages && !o.pdfSingle && !o.weekly && quadChunks.length > 30 && !confirm('This will save ' + quadChunks.length + ' page images. Continue?')) { hideProgress(); return 'cancelled'; }

    setProgressPlan((o.pdfSingle ? products.length : 0) + (doQuad ? quadChunks.length : 0) + (o.popupImages ? products.length : 0));

    /* start downloading images (at render size) in the background, in the order they're needed */
    var urls = [];
    function addThumbs(p) { extraThumbs(allImagesOf(p), 5).forEach(function (u) { urls.push(sizedUrl(u, IMG_THUMB_W)); }); }
    if (o.pdfSingle || o.popupImages) products.forEach(function (p) { var a = allImagesOf(p); if (a[0]) urls.push(sizedUrl(a[0], IMG_MAIN_W)); addThumbs(p); });
    if (doQuad) products.forEach(function (p) { var a = allImagesOf(p); if (a[0]) urls.push(sizedUrl(a[0], IMG_QUAD_W)); addThumbs(p); });
    prefetchImagesInBackground(urls);

    var JSZipLib = await zipPromise;
    var sink = wantsImages ? createImageSink(JSZipLib, o.weekly ? 'Weekly_Ads_Images' : 'Catalog_Images', stamp) : null;
    var stages = [o.pdfSingle, doQuad, o.popupImages].filter(Boolean).length, stageNo = 0;
    function stageLabel(t) { return (o.weekly || stages > 1 ? 'Step ' + stageNo + '/' + stages + ' — ' : '') + t; }
    var jsPDF = window.jspdf && window.jspdf.jsPDF;

    /* ---- Stage: one product per PDF page ---- */
    if (o.pdfSingle) {
      stageNo++; setProgressLabel(stageLabel('Product pages (PDF)'));
      var pages = await runPool(products, RENDER_CONCURRENCY, async function (p) {
        try { return await renderCardToJpeg(buildProductCardHTML(p, SINGLE_PAGE_OPTS), PDF_PAGE_W, PDF_PAGE_H, Q_SINGLE); }
        catch (e) { failures++; console.warn('Page failed:', p.title, e); return null; }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
      var doc = new jsPDF(), pw = doc.internal.pageSize.getWidth(), ph = doc.internal.pageSize.getHeight(), n = 0;
      pages.forEach(function (u) { if (!u) return; if (n++ > 0) doc.addPage(); doc.addImage(u, 'JPEG', 0, 0, pw, ph, undefined, 'FAST'); });
      if (n) { savePdfDoc(doc, 'Selected_Products'); summary.push('📄 Selected_Products.pdf — ' + n + ' page(s)'); }
    }

    /* ---- Stage: 4-per-page sheets — rendered ONCE, used for PDF and/or images ---- */
    if (doQuad) {
      stageNo++; setProgressLabel(stageLabel('4-per-page sheets'));
      var sheets = await runPool(quadChunks, RENDER_CONCURRENCY, async function (c) {
        try { return await renderCardToJpeg(buildQuadPageHTML(c), 0, 0, Q_QUAD); }
        catch (e) { failures++; console.warn('Sheet failed', e); return null; }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
      if (o.pdfQuad) {
        var qd = new jsPDF(), qw = qd.internal.pageSize.getWidth(), qh = qd.internal.pageSize.getHeight(), qn = 0;
        sheets.forEach(function (u) { if (!u) return; if (qn++ > 0) qd.addPage(); qd.addImage(u, 'JPEG', 0, 0, qw, qh, undefined, 'FAST'); });
        if (qn) { savePdfDoc(qd, 'Selected_Products_4up'); summary.push('📄 Selected_Products_4up.pdf — ' + qn + ' sheet(s)'); }
      }
      if (o.quadImages) {
        for (var si = 0; si < sheets.length; si++) if (sheets[si]) await sink.add('Catalog_Pages', 'Catalog_Page_' + String(si + 1).padStart(2, '0') + '.jpg', sheets[si]);
      }
    }

    /* ---- Stage: individual product images ---- */
    if (o.popupImages) {
      stageNo++; setProgressLabel(stageLabel('Product images'));
      var used = new Set();
      await runPool(products, RENDER_CONCURRENCY, async function (p) {
        try {
          var u = await renderPopupToJpeg(buildPopupData(p), Q_POPUP);
          var base = safeFileName(p.title), name = base, k = 2;
          while (used.has(name)) name = base + '_' + (k++);
          used.add(name);
          await sink.add('Product_Images', name + '.jpg', u);
        } catch (e) { failures++; console.warn('Image failed:', p.title, e); }
        finally { tickProgress(); }
      });
      if (bulkCancel) { hideProgress(); return 'cancelled'; }
    }

    if (sink && sink.count()) {
      var zipName = await sink.finish();
      summary.push(zipName ? '🖼️ ' + sink.count() + ' image(s) bundled in ' + zipName : '🖼️ ' + sink.count() + ' image(s) downloaded individually');
    }
    if (failures) summary.push('⚠️ ' + failures + ' item(s) failed to render (see browser console)');
    finishProgress(summary.join('\n') || 'Nothing was exported.');
    return 'ok';
  } catch (e) {
    console.error('Export failed:', e);
    hideProgress();
    alert('Export failed: ' + (e && e.message ? e.message : e));
    return 'error';
  } finally {
    endBulkOp();
  }
}

/* Button entry points (same names the page already uses) */
function exportSelectedToPDF()      { return runExports({ pdfSingle: true }); }
function exportSelectedToPDF4up()   { return runExports({ pdfQuad: true }); }
function splitFourUpToImages()      { return runExports({ quadImages: true }); }
function saveImagesForSelection()   { return runExports({ popupImages: true }); }
function runWeeklyAdsExport()       { return runExports({ pdfSingle: true, pdfQuad: true, quadImages: true, popupImages: true, weekly: true }); }

/* ---------------- init ---------------- */
document.addEventListener('DOMContentLoaded', function () {
  localStorage.removeItem('selectedProducts');
  document.querySelectorAll('.product-checkbox').forEach(function (cb) { cb.checked = false; });
  document.querySelectorAll('.order-qty-input').forEach(function (i) { i.value = 0; });
  var tbody = document.getElementById('product-table-body');
  if (tbody) initialRowsHTML = tbody.innerHTML;
  initTableEvents();
  toggleClearButton();
  updateSelectedCount();
  var pc = document.querySelector('.popup-close'); if (pc) pc.addEventListener('click', closePopup);
  var cb = _el('bulk-cancel-btn'); if (cb) cb.addEventListener('click', cancelBulkOp);
  var xb = _el('bulk-close-btn'); if (xb) xb.addEventListener('click', closeProgress);
  if ('requestIdleCallback' in window) requestIdleCallback(function () { loadJsZip(); });
});
